import { useRef, useState } from 'react'
import { AtSign, X } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { InfoCard } from '@/components/ui/InfoCard'
import { errorToast } from '../../../occurrences/utils/error-toast'
import {
  useMentionCandidatesForIssue,
  useTagMentions,
  useUntagMention,
} from '../../hooks/useSystemIssueMentions'
import { Avatar } from '../../../occurrences/components/common/Avatar'
import { PersonPicker } from '../../../occurrences/components/common/PersonPicker'
import type { SystemIssue } from '../../types/system-issues'

const MAX_AVATARS = 4

/**
 * Card "Colegas marcados" (veem e conversam no chamado): avatares
 * sobrepostos; clicar abre o painel com quem já está marcado (e "remover")
 * em cima e a busca para marcar mais embaixo — só quem ainda não está
 * marcado, e o painel fica aberto para marcar vários. Tirar a marcação:
 * quem abriu, dev, ou a própria pessoa.
 */
export function IssueMentionsCard({ issue, canManage }: { issue: SystemIssue; canManage: boolean }) {
  const { user } = useAuth()
  const [open, setOpen] = useState(false)
  // Escolhido agora: sai da lista na hora, antes de o chamado recarregar.
  const [justTagged, setJustTagged] = useState<number[]>([])
  const cardRef = useRef<HTMLButtonElement>(null)
  const {
    data: candidates = [],
    isLoading,
    isError,
  } = useMentionCandidatesForIssue(open ? issue.id : null)
  const tag = useTagMentions(issue.id)
  const untag = useUntagMention(issue.id)
  const mentioned = issue.mentioned ?? []
  const mentionedIds = mentioned.map((m) => m.id)
  const canRemove = (id: number) => issue.created_by_id === user?.id || canManage || id === user?.id

  const available = candidates.filter(
    (c) => !mentionedIds.includes(c.id) && !justTagged.includes(c.id),
  )
  // `mutateAsync` (não `mutate` com callbacks): marcando vários seguidos, o
  // react-query só chama o `onError` da ÚLTIMA chamada — uma falha anterior
  // passaria calada. A promessa de cada chamada é dela.
  const pick = (id: number) => {
    setJustTagged((ids) => [...ids, id])
    tag.mutateAsync([id]).catch((err: unknown) => {
      setJustTagged((ids) => ids.filter((x) => x !== id))
      errorToast('Não foi possível marcar o colega.')(err)
    })
  }
  const remove = (id: number) => {
    // Volta para a lista na hora (se tinha acabado de ser marcado).
    setJustTagged((ids) => ids.filter((x) => x !== id))
    untag.mutateAsync(id).catch(errorToast('Não foi possível tirar a marcação.'))
  }

  const extra = mentioned.length - MAX_AVATARS

  return (
    <>
      <InfoCard
        label="Colegas marcados"
        buttonRef={cardRef}
        onClick={() => setOpen(true)}
        actionTitle={
          mentioned.length > 0
            ? `Marcados: ${mentioned.map((m) => m.full_name).join(', ')}`
            : 'Marcar colega'
        }
      >
        {mentioned.length > 0 ? (
          <span className="flex items-center">
            <span className="flex -space-x-1.5">
              {mentioned.slice(0, MAX_AVATARS).map((m) => (
                <Avatar
                  key={m.id}
                  name={m.full_name}
                  picture={m.picture}
                  size={26}
                  variant="soft"
                  className="ring-2 ring-surface"
                />
              ))}
            </span>
            {extra > 0 && (
              <span className="ml-1.5 text-sm font-semibold text-muted">+{extra}</span>
            )}
          </span>
        ) : (
          <span className="flex items-center gap-1.5 text-muted">
            <AtSign className="h-4 w-4" />
            Marcar colega
          </span>
        )}
      </InfoCard>
      {open && (
        <PersonPicker
          anchorRef={cardRef}
          alignRight
          onClose={() => {
            setOpen(false)
            setJustTagged([])
          }}
          loading={isLoading}
          error={isError}
          searchPlaceholder="Marcar colega…"
          emptyText={
            candidates.length > 0
              ? 'Todos os colegas já estão marcados.'
              : 'Nenhum colega para marcar.'
          }
          header={
            mentioned.length > 0 && (
              <div className="border-b border-border-subtle p-1">
                <p className="px-2.5 pb-1 pt-1.5 text-[11px] font-semibold uppercase tracking-wide text-subtle">
                  Marcados
                </p>
                <ul>
                  {mentioned.map((m) => (
                    <li
                      key={m.id}
                      className="flex items-center gap-2 rounded-md px-2.5 py-1.5 text-[13px] text-body"
                    >
                      <Avatar name={m.full_name} picture={m.picture} size={24} variant="soft" />
                      <span className="min-w-0 flex-1 truncate">{m.full_name}</span>
                      {canRemove(m.id) && (
                        <button
                          type="button"
                          onClick={() => remove(m.id)}
                          disabled={untag.isLoading}
                          aria-label={`Remover marcação de ${m.full_name}`}
                          title="Remover marcação"
                          className="rounded p-0.5 text-subtle transition hover:bg-surface-muted hover:text-body disabled:opacity-50"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )
          }
          people={available.map((c) => ({
            id: c.id,
            name: c.name,
            subtitle: c.role,
            picture: c.google_picture ?? c.microsoft_picture,
          }))}
          onPick={pick}
        />
      )}
    </>
  )
}
